import { debounce, requestIdleCallback, viewTransition } from '@theme/utilities';

const OFFSET = 40;

/**
 * A custom element that manages a floating panel.
 * v231: when parent details is closed, remove from hit-testing entirely (hidden + clear coords).
 */
export class FloatingPanelComponent extends HTMLElement {
  #updatePosition = async () => {
    if (viewTransition.current) await viewTransition.current;

    const parent = this.closest('details');
    if (parent instanceof HTMLDetailsElement && !parent.open) {
      this.style.removeProperty('top');
      this.style.removeProperty('left');
      this.setAttribute('hidden', '');
      this.setAttribute('inert', '');
      this.#mutationObserver.takeRecords();
      return;
    }

    this.removeAttribute('hidden');
    this.removeAttribute('inert');

    const rect = this.getBoundingClientRect();
    const viewportWidth = window.innerWidth;

    this.style.top = OFFSET + 'px';

    if (rect.right > viewportWidth) {
      const overflowAmount = rect.right - viewportWidth + OFFSET;
      this.style.left = `-${overflowAmount}px`;
    }

    if (rect.left < 0) {
      const overflowAmount = Math.abs(rect.left) + OFFSET;
      this.style.left = `${overflowAmount}px`;
    }

    this.#mutationObserver.takeRecords();
  };

  #onDetailsToggle = () => {
    this.#updatePosition();
  };

  #mutationObserver = new MutationObserver(this.#updatePosition);

  #resizeListener = debounce(() => {
    const parent = this.closest('details');
    const closeOnResize = this.dataset.closeOnResize === 'true';
    if (parent instanceof HTMLDetailsElement && closeOnResize) {
      parent.open = false;
      parent.removeAttribute('open');
      this.#updatePosition();
    }
  }, 100);

  connectedCallback() {
    window.addEventListener('resize', this.#resizeListener);
    const parent = this.closest('details');
    if (parent instanceof HTMLDetailsElement) {
      parent.addEventListener('toggle', this.#onDetailsToggle);
    }

    requestIdleCallback(() => {
      this.#updatePosition();
      this.#mutationObserver.observe(this, { attributes: true, attributeFilter: ['class', 'style'] });
    });
  }

  disconnectedCallback() {
    window.removeEventListener('resize', this.#resizeListener);
    const parent = this.closest('details');
    if (parent instanceof HTMLDetailsElement) {
      parent.removeEventListener('toggle', this.#onDetailsToggle);
    }
    this.#mutationObserver.disconnect();
  }
}

if (!customElements.get('floating-panel-component')) {
  customElements.define('floating-panel-component', FloatingPanelComponent);
}
